import { usePress } from "@react-aria/interactions";
import { AriaMenuTriggerProps, useMenuTrigger } from "@react-aria/menu";
import { AriaPopoverProps, Overlay, usePopover } from "@react-aria/overlays";
import { mergeProps } from "@react-aria/utils";
import { MenuTriggerProps, useMenuTriggerState } from "@react-stately/menu";
import { MenuTriggerType } from "@react-types/menu";
import React, {
  HTMLAttributes,
  ReactNode,
  RefObject,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import styled from "styled-components";
import {
  FadeInAnimation,
  FadeOutAnimation,
} from "../../shared/animations/FadeAnimation";
import { MenuRoot, MenuRootProps } from "./MenuRoot";

/** Props passed to the `trigger` render function of the dropdown. */
export type MenuDropdownTriggerProps = {
  triggerProps: HTMLAttributes<HTMLElement> & {
    ref: RefObject<HTMLButtonElement>;
  };
};

/** A render prop function that renders the trigger element for the dropdown. */
export type MenuDropdownTriggerRenderProp = (
  props: MenuDropdownTriggerProps,
) => ReactNode;

/** Props for the MenuDropdown component. */
export type MenuDropdownProps<T extends object> = MenuRootProps<T> &
  Omit<MenuTriggerProps, "trigger"> &
  Omit<AriaMenuTriggerProps, "trigger"> &
  Pick<
    AriaPopoverProps,
    | "crossOffset"
    | "isKeyboardDismissDisabled"
    | "isNonModal"
    | "maxHeight"
    | "offset"
    | "placement"
    | "scrollRef"
    | "shouldCloseOnInteractOutside"
    | "shouldFlip"
    | "shouldUpdatePosition"
  > & {
    /**
     * A function that renders the trigger element for the dropdown. The
     * function is called with an object containing `triggerProps` that should
     * be spread onto the trigger element.
     *
     * @example
     *
     * ```tsx
     * <Menu.Dropdown trigger={({ triggerProps }) => <button {...triggerProps}>Open Menu</button>}>
     * ```
     */
    trigger: MenuDropdownTriggerRenderProp;
    /**
     * How the menu is triggered.
     *
     * @default "press"
     */
    triggerType?: MenuTriggerType;
  };

export function MenuDropdown<T extends object>({
  trigger,
  triggerType = "press",
  ...otherProps
}: MenuDropdownProps<T>) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLUListElement>(null);

  const menuTriggerState = useMenuTriggerState({
    ...otherProps,
    trigger: triggerType,
  });
  const { menuTriggerProps, menuProps } = useMenuTrigger<T>(
    { ...otherProps, trigger: triggerType },
    menuTriggerState,
    triggerRef,
  );

  const { pressProps: triggerProps } = usePress(menuTriggerProps);

  const { popoverProps } = usePopover(
    {
      containerPadding: 24,
      offset: 8,
      placement: "bottom start",
      ...otherProps,
      popoverRef,
      triggerRef,
    },
    menuTriggerState,
  );

  const [isExiting, setIsExiting] = useState(false);

  useLayoutEffect(() => {
    setIsExiting(!menuTriggerState.isOpen);
  }, [menuTriggerState.isOpen]);

  const handleAnimationEnd = () => {
    setIsExiting(false);
  };

  return (
    <>
      {trigger({ triggerProps: { ...triggerProps, ref: triggerRef } })}
      {(menuTriggerState.isOpen || isExiting) && (
        <Overlay isExiting={isExiting}>
          <StyledMenuRoot
            {...mergeProps(popoverProps, menuProps, otherProps)}
            isExiting={isExiting}
            onAnimationEnd={handleAnimationEnd}
            ref={popoverRef}
          />
        </Overlay>
      )}
    </>
  );
}

const StyledMenuRoot = styled(MenuRoot)<{ isExiting: boolean }>`
  animation: ${({ isExiting }) =>
      isExiting ? FadeOutAnimation : FadeInAnimation}
    0.1s ease-out forwards;
  overflow: auto;
`;
